/**
 * Forces several classes of asynchronously queued tasks to flush:
 * - Debouncers added via `enqueueDebouncer`
 * - ShadyDOM distribution
 *
 * @return {void}
 */
export const COMCalendarDateFormatter = {
  /** 
    * @desc Method to set the avail dates styles and either disable/enable the calendar per each day
    * @param {String} dateString
    * @param {Object} availableDates
    * @param {Object} dateTypeMap
    * @param {Boolean} isVariableParks
    * @param {Boolean} unblockFutureDates
    * @param {Boolean} setCalendarStylesToBlocked
   */
  getAvailDate: function(dateString, availableDates, dateTypeMap, isVariableParks = false, unblockFutureDates = false, setCalendarStylesToBlocked = false) {
    let dateToAdd = {};

    if (availableDates[dateString]) {
      dateToAdd = availableDates[dateString];
      // if date is marked as disabled, retain that value
      if (!availableDates[dateString].disabled) {
        dateToAdd.disabled = false;
      }
    } else {
      dateToAdd = {
        date: dateString,
        dateType: dateTypeMap.none,
        disabled: true
      };
      availableDates[dateString] = dateToAdd;
    }
    dateToAdd.unavailable = false;

    if (dateToAdd.facilities && dateToAdd.facilities.length > 0) {
      let dateType = dateTypeMap.none;
      let availableFacilities = dateToAdd.facilities.filter(element => element.available || element.hasAvailability);
      let blockedFacilities = dateToAdd.facilities.filter(element => element.blocked);
      let bonusFacilities = dateToAdd.facilities.filter(element => element.bonusDay);

      if (availableFacilities.length > 0) {
        dateType = isVariableParks || unblockFutureDates ?
          _setAvailVariableDateType(availableFacilities, dateTypeMap, blockedFacilities, bonusFacilities, dateToAdd.facilities.length) :
          _setAvailDateType(availableFacilities, dateTypeMap);
      } else {
        if (isVariableParks || unblockFutureDates) {
          dateType = blockedFacilities.length === dateToAdd.facilities.length ? dateTypeMap.blocked : dateTypeMap.noReservations;
          // we use unavailable property to enable the selection of unavailable dates for GSS cast flow
          // if the dateType === noReservations we set it to true otherwise it stays as false
          dateToAdd.unavailable = (dateType === dateTypeMap.noReservations);
          dateToAdd.disabled = true;
        } else {
          dateType = (!!dateTypeMap.blocked && blockedFacilities.length === dateToAdd.facilities.length) ? dateTypeMap.blocked : dateTypeMap.unavailable;
          dateToAdd.unavailable = true;
          dateToAdd.disabled = true;
        }
        if (setCalendarStylesToBlocked) {
          // if setCalendarStylesToBlocked is true, we need to set the unavailable dates to blocked style
          dateType = dateTypeMap.blocked;
          dateToAdd.unavailable = true;
          dateToAdd.disabled = true;
        }
      }

      if (dateToAdd.availability === 'aplex-key-availability' 
        && (dateType.class === 'all' || dateType.class === 'primary' || dateType.class === 'secondary')) {
        dateType = dateTypeMap.goodToGo;
      }

      dateToAdd.dateType = dateType;

      // Facilities will be managed as good-to-go only if there is at least one non-blocked facility
      const goodToGoFacilities =
        dateToAdd.facilities.filter(element => element.hasReservationNotRequiredOptions);
      const hasNonBlockedFacility =
        !!dateToAdd.facilities.find(element => !element.blocked);
      // Retrieve those good-to-go facilities that are unavailable but not blocked
      const goodToGoNotAvailableFacilities = dateToAdd.facilities.filter(element =>
        element.hasReservationNotRequiredOptions && !element.available && !element.blocked
      );

      if (hasNonBlockedFacility && dateTypeMap.goodToGoAll && goodToGoFacilities && goodToGoFacilities.length) {
        dateToAdd.dateType = _getGoodToGoDateType(
          dateTypeMap,
          dateToAdd.facilities.length,
          availableFacilities,
          goodToGoNotAvailableFacilities
        );
      }
    }
    return dateToAdd;
  }
};

/**
 * This function sets the date type when we just have two parks to reserve.
 * @param {Array} availableFacilities
 * @param {Array} dateTypeMap
 * @returns Object
 */
function _setAvailDateType(availableFacilities, dateTypeMap) {
  let dateType = {};
  if (availableFacilities.length === 1) {
    dateType = dateTypeMap[availableFacilities[0].facilityName] ? dateTypeMap[availableFacilities[0].facilityName] : dateType;
  } else {
    dateType = dateTypeMap.all;
  }
  return dateType;
}

/**
 * This function sets the date type when we just have four parks to reserve,
 * taking into account whether parks are blocked out or not
 * @param {Array} availableFacilities
 * @param {Array} dateTypeMap
 * @param {Array} blockedFacilities
 * @param {Array} bonusFacilities
 * @param {Number} facilitiesLength
 * @returns Object
 */
function _setAvailVariableDateType(availableFacilities, dateTypeMap, blockedFacilities, bonusFacilities, facilitiesLength) {
  let dateType = {};
  if (blockedFacilities.length === facilitiesLength) {
    dateType = dateTypeMap.blocked;
  } else if (bonusFacilities.length > 0) {
    dateType = dateTypeMap.bonus;
  } else if (blockedFacilities.length > 0 || availableFacilities.length < facilitiesLength) {
    dateType = dateTypeMap.some;
  } else if (blockedFacilities.length === 0 && availableFacilities.length === facilitiesLength) {
    dateType = dateTypeMap.all;
  }
  return dateType;
}

/**
 * @desc Retrieve dateType based on facilities length and good-to-go facilities.
 * @param {Array} dateTypeMap
 * @param {Number} facilitiesLength
 * @param {Array} availableFacilities
 * @param {Array} goodToGoNotAvailableFacilities
 * @return {Object}
 */
function _getGoodToGoDateType(
  dateTypeMap,
  facilitiesLength,
  availableFacilities,
  goodToGoNotAvailableFacilities
) {
  const totalAvailableFacilities = goodToGoNotAvailableFacilities.length + availableFacilities.length;

  // Date indicator (small icon under the date) will be set as goodToGoAll on the following scenarios:
  // Scenario 1: All the facilities are in a good-to-go non-blocked state
  // Scenario 3: All the facilities are available regardless good-to-go state
  return (totalAvailableFacilities === facilitiesLength) ||
    (availableFacilities === facilitiesLength)
      ? dateTypeMap.goodToGoAll
      : dateTypeMap.goodToGoSome;
}
